import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpenCheck, ListChecks, MessageCircle, Target, TrendingUp, Trophy, X, Zap } from "lucide-react";
import { PortalShell, usePortalAccount } from "./PortalShell";
import { portalApi, portalErr, openHomework } from "./portalApi";
import { useBuy } from "./PortalStore";
import "../test/testTheme.css";

const fmtDate = (ts) => new Date(ts * 1000).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });

const LEVEL_COLORS = { 1: "#f7b500", 2: "#2e6ff2", 3: "#12b76a" };
const mixText = (mix) => ["Easy", "Medium", "Hard"].filter((k) => mix?.[k]).map((k) => `${mix[k]} ${k}`).join(" · ");
const WA_99 = "https://wa.me/918299549437?text=Hi%20Pranjal%20Sir!%20I%20want%20to%20unlock%20personal%20Physics%20help%20(%E2%82%B999).";

const FREE_TEST_CLASSES = [
  { key: "class_9", label: "Class 9" }, { key: "class_10", label: "Class 10" },
  { key: "class_11", label: "Class 11" }, { key: "class_12", label: "Class 12" },
  { key: "neet", label: "NEET" }, { key: "jee", label: "JEE" },
];

const HW_STATUS_META = {
  draft: ["Draft", "draft"], submitted: ["Submitted — waiting for review", "review"],
  reviewing: ["Being checked by Pranjal Sir", "review"], evaluated: ["Evaluated", "evaluated"],
  published: ["Evaluated", "evaluated"], returned: ["Returned for corrections", "returned"],
  resubmission_requested: ["Resubmit requested", "returned"],
};

function LevelMeter({ level }) {
  return (
    <div style={{ display: "flex", gap: 4, flex: 1, maxWidth: 220 }}>
      {[1, 2, 3].map((seg) => (
        <div key={seg} style={{ flex: 1, height: 10, borderRadius: 5, background: level >= seg ? LEVEL_COLORS[level] : "#e8edf4", transition: "background .3s" }} />
      ))}
    </div>
  );
}

function FreeTestModal({ onClose }) {
  const [picked, setPicked] = useState("");
  return (
    <div className="ph-modal-backdrop" onClick={onClose} data-testid="free-test-modal">
      <div className="ph-modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div className="tp-eyebrow">FREE DIAGNOSTIC TEST</div>
            <h2 style={{ margin: "6px 0 0", font: "700 24px Outfit", color: "#fff", letterSpacing: "-.01em" }}>Choose your class</h2>
          </div>
          <button className="ph-modal-close" onClick={onClose} data-testid="free-test-close" aria-label="Close"><X size={17} /></button>
        </div>
        <p style={{ color: "#9EADBD", fontSize: 13, margin: "8px 0 18px" }}>5 questions · instant result with solutions · free forever</p>
        <div className="ph-class-grid">
          {FREE_TEST_CLASSES.map((c) => (
            <button key={c.key} className={`ph-class-card${picked === c.key ? " active" : ""}`}
                    onClick={() => setPicked(c.key)} data-testid={`class-picker-${c.key}`}>{c.label}</button>
          ))}
        </div>
        <a className={`ph-start-btn${picked ? "" : " disabled"}`}
           href={picked ? "/physics-test" : undefined}
           onClick={(e) => { if (!picked) e.preventDefault(); }}
           data-testid="free-test-start">
          START MY FREE TEST <Zap size={16} />
        </a>
      </div>
    </div>
  );
}

function PortalHomeInner() {
  const account = usePortalAccount();
  const [data, setData] = useState(null);
  const [levels, setLevels] = useState(null);
  const [hw, setHw] = useState(null);
  const [error, setError] = useState("");
  const [showFreeTest, setShowFreeTest] = useState(false);
  const [paymentsLive, setPaymentsLive] = useState(false);
  const [helpOwned, setHelpOwned] = useState(false);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [myReview, setMyReview] = useState(undefined);
  const [reviewForm, setReviewForm] = useState({ rating: 5, text: "" });
  const [reviewMsg, setReviewMsg] = useState("");
  const { buy, buying, error: buyErr } = useBuy(() => { setJustUnlocked(true); setHelpOwned(true); });

  useEffect(() => {
    portalApi.get("/portal/overview").then(({ data: d }) => setData(d)).catch(() => setError("Could not load your dashboard. Please refresh."));
    portalApi.get("/portal/levels").then(({ data: d }) => setLevels(d)).catch(() => {});
    portalApi.get("/portal/hw-status").then(({ data: d }) => setHw(d)).catch(() => setHw({ linked: false, submissions: [] }));
    portalApi.get("/reviews/mine").then(({ data: d }) => setMyReview(d.review)).catch(() => setMyReview(null));
    portalApi.get("/store/products").then(({ data: d }) => setPaymentsLive(!!d.payments_live)).catch(() => {});
    portalApi.get("/store/my-purchases").then(({ data: d }) => {
      const ents = d.purchases || [];
      if (ents.some((e) => e.item_id === "personal_help_99")) setHelpOwned(true);
    }).catch(() => {});
  }, []);

  const latest = data?.recent?.[0];

  return (
    <div className="tp-wrap" style={{ maxWidth: 980, padding: "40px 22px 70px" }} data-testid="portal-home">
      <div className="tp-eyebrow">MY DASHBOARD</div>
      <h1 className="tp-h2" style={{ fontSize: 34 }} data-testid="portal-home-title">Hi {account.name.split(" ")[0]}, ready for today&apos;s practice?</h1>
      {error && <p className="tp-error" role="alert" data-testid="portal-home-error">{error}</p>}
      {!data && !error && <p className="tp-muted">Loading your stats…</p>}

      {data && <>
        {/* BENTO ROW: performance hero + free test */}
        <div className="ph-bento" data-testid="portal-hero-row">
          <section className="ph-perf-card" data-testid="portal-performance">
            <div className="ph-perf-glow" />
            <div className="tp-eyebrow" style={{ color: "#FFBF69" }}>YOUR PERFORMANCE</div>
            {latest ? (
              <div className="ph-perf-body">
                <div className="ph-perf-score" data-testid="portal-latest-score">
                  <span className="ph-perf-big">{latest.accuracy}<small>%</small></span>
                  <span className="ph-perf-cap">latest accuracy · {latest.topic_name}</span>
                </div>
                <div className="ph-perf-stats">
                  <div><b data-testid="portal-stat-quizzes">{data.quizzes_taken}</b><span>quizzes done</span></div>
                  <div><b data-testid="portal-stat-accuracy">{data.avg_accuracy}%</b><span>avg accuracy</span></div>
                  <div><b data-testid="portal-stat-best">{data.best_score}/20</b><span>best score</span></div>
                  <div><b>{data.topics_tried}</b><span>topics practised</span></div>
                </div>
              </div>
            ) : (
              <div className="ph-perf-body"><div className="ph-perf-cap" style={{ fontSize: 15 }} data-testid="portal-performance-empty">
                No tests yet — take your first free test and your performance will light up right here.</div></div>
            )}
            <Link className="ph-perf-link" to="/portal/quiz" data-testid="portal-start-quiz">Start a 5-question quiz <ArrowRight size={15} /></Link>
          </section>

          <section className="ph-test-card" data-testid="portal-free-test-card">
            <div className="ph-test-atom" />
            <Zap size={26} color="#FF9F1C" />
            <h3>Free Physics Test</h3>
            <p>Test your concepts, discover your mistakes — instantly.</p>
            <button className="ph-test-btn" onClick={() => setShowFreeTest(true)} data-testid="take-physics-test-btn">TAKE FREE TEST</button>
          </section>
        </div>

        {/* ₹99 UNLOCK BANNER */}
        <section className="ph-unlock" data-testid="portal-unlock-banner">
          <div className="ph-unlock-glow" />
          <div style={{ flex: 1, minWidth: 240 }}>
            <b style={{ fontSize: 16 }}>{helpOwned ? "Pranjal Sir's personal help — unlocked" : "Unlock Pranjal Sir's personal help — just ₹99"}</b>
            <p style={{ margin: "4px 0 0", color: "#c8d4e2", fontSize: 13 }}>
              {helpOwned ? "Message anytime with any Physics doubt — it gets solved personally, one-to-one."
                         : "Stuck on any Physics doubt? Get it solved personally, one-to-one."}
            </p>
            {buyErr && <p style={{ margin: "6px 0 0", color: "#ffb4ab", fontSize: 12 }} data-testid="unlock-buy-error">{buyErr}</p>}
          </div>
          {helpOwned ? (
            <a className="ph-wa-btn" href={WA_99} target="_blank" rel="noreferrer" data-testid="promo-whatsapp-btn">
              <MessageCircle size={16} /> Message Pranjal Sir
            </a>
          ) : paymentsLive ? (
            <button className="ph-wa-btn" style={{ border: 0, cursor: "pointer" }} disabled={!!buying}
                    onClick={() => buy("product", "personal_help_99", "Pranjal Sir's Personal Physics Help")}
                    data-testid="unlock-buy-btn">
              {buying ? "Opening payment…" : "Unlock for ₹99"}
            </button>
          ) : (
            <a className="ph-wa-btn" href={WA_99} target="_blank" rel="noreferrer" data-testid="promo-whatsapp-btn">
              <MessageCircle size={16} /> Unlock on WhatsApp
            </a>
          )}
        </section>

        {/* HOMEWORK STATUS */}
        <section className="tp-panel" style={{ marginBottom: 18 }} data-testid="portal-homework">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <h2 className="tp-h2" style={{ fontSize: 20, margin: 0 }}><BookOpenCheck size={18} style={{ verticalAlign: "-3px", marginRight: 6 }} />My Homework</h2>
            <a className="tp-btn small" href="/homework" onClick={(e) => { e.preventDefault(); openHomework(); }} data-testid="portal-open-homework">Open Homework App <ArrowRight size={14} /></a>
          </div>
          {!hw ? <p className="tp-muted">Loading homework…</p>
            : !hw.linked ? (
              <p className="tp-muted" data-testid="portal-hw-unlinked">
                No homework account linked yet — <a href="/homework" onClick={(e) => { e.preventDefault(); openHomework(); }} style={{ color: "#f77f00", fontWeight: 700 }}>open the Homework app</a> and your account is created automatically — your submissions will show here.
              </p>
            ) : hw.submissions.length === 0 ? (
              <p className="tp-muted" data-testid="portal-hw-empty">No homework submitted yet — your submissions and Pranjal Sir&apos;s marks will appear here.</p>
            ) : (
              <div style={{ marginTop: 10 }} data-testid="homework-status-list">
                {hw.submissions.map((s, i) => {
                  const [label, tone] = HW_STATUS_META[s.status] || [s.status, "draft"];
                  return (
                    <div key={s.submission_id} className="ph-hw-row" data-testid={`portal-hw-row-${i}`}>
                      <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{s.title}</span>
                      <span className={`ph-pill ${tone}`} data-testid={`portal-hw-status-${i}`}>{label}</span>
                      {s.score_10 != null && <b style={{ fontFamily: "Outfit", color: "#0b2545" }} data-testid={`portal-hw-score-${i}`}>{s.score_10}/10</b>}
                      <span className="tp-note">{s.updated_at ? fmtDate(s.updated_at) : ""}</span>
                    </div>
                  );
                })}
              </div>
            )}
        </section>

        {/* WRITE A REVIEW */}
        <section className="tp-panel" style={{ marginBottom: 18 }} data-testid="portal-review">
          <h2 className="tp-h2" style={{ fontSize: 20, margin: 0 }}>Loved learning with Pranjal Sir?</h2>
          <p className="tp-note" style={{ margin: "4px 0 12px" }}>Write a short review — it appears on the website after Pranjal Sir reads and approves it.</p>
          {myReview && (
            <p className="tp-note" data-testid="review-existing">Your review: <b>{myReview.status === "approved" ? "live on the site" : myReview.status === "rejected" ? "not approved" : "waiting for approval"}</b> — you can update it below.</p>
          )}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }} data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                      style={{ background: "none", border: 0, cursor: "pointer", fontSize: 26, color: n <= reviewForm.rating ? "#f7b500" : "#d7dee8", padding: 2 }}
                      data-testid={`review-star-${n}`}>★</button>
            ))}
          </div>
          <textarea className="admin-input" rows={3} style={{ width: "100%", resize: "vertical" }}
                    placeholder="What improved in your Physics after studying with Pranjal Sir?"
                    value={reviewForm.text} onChange={(e) => setReviewForm((f) => ({ ...f, text: e.target.value }))}
                    data-testid="review-text" />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
            <button className="tp-btn" disabled={reviewForm.text.trim().length < 10}
                    onClick={async () => {
                      setReviewMsg("");
                      try {
                        const { data: r } = await portalApi.post("/reviews", reviewForm);
                        setReviewMsg(r.message);
                        const { data: d } = await portalApi.get("/reviews/mine");
                        setMyReview(d.review);
                      } catch (e2) { setReviewMsg(portalErr(e2)); }
                    }}
                    data-testid="review-submit">{myReview ? "UPDATE REVIEW" : "SUBMIT REVIEW"}</button>
            {reviewMsg && <span className="tp-note" data-testid="review-msg">{reviewMsg}</span>}
          </div>
        </section>

        {/* STATS GRID (kept) */}
        <div className="tp-cards" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", margin: "6px 0 22px" }} data-testid="portal-stats">
          <div className="tp-card"><div className="tp-ico"><ListChecks size={20} /></div><h3>{data.quizzes_taken}</h3><p>Quizzes completed</p></div>
          <div className="tp-card"><div className="tp-ico"><Target size={20} /></div><h3>{data.avg_accuracy}%</h3><p>Overall accuracy</p></div>
          <div className="tp-card"><div className="tp-ico"><Trophy size={20} /></div><h3>{data.best_score}/20</h3><p>Best score</p></div>
          <div className="tp-card"><div className="tp-ico"><TrendingUp size={20} /></div><h3>{data.topics_tried}</h3><p>Topics practised</p></div>
        </div>

        {levels && levels.summary.in_progress + levels.summary.exam_ready > 0 && (
          <section className="tp-panel" style={{ marginBottom: 18 }} data-testid="portal-levels">
            <h2 className="tp-h2" style={{ fontSize: 20 }}>Your level per chapter</h2>
            <p className="tp-note" style={{ margin: "4px 0 12px" }}>{levels.summary.exam_ready} exam-ready · {levels.summary.in_progress} in progress · {levels.summary.not_started} not started — Adaptive quizzes use this to set your difficulty.</p>
            {levels.topics.filter((t) => t.level > 0).map((t, i) => (
              <div key={t.topic_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #eef1f5", flexWrap: "wrap" }} data-testid={`portal-level-row-${i}`}>
                <span style={{ width: 180, fontWeight: 600, fontSize: 14 }}>{t.name}</span>
                <LevelMeter level={t.level} />
                <span className="tp-pill" style={{ background: `${LEVEL_COLORS[t.level]}1a`, color: LEVEL_COLORS[t.level] }} data-testid={`portal-level-label-${i}`}>{t.level_label.toUpperCase()}{t.accuracy != null ? ` · ${t.accuracy}%` : ""}</span>
                {t.target_mix && <span className="tp-note" data-testid={`portal-level-mix-${i}`}>next: {mixText(t.target_mix)}</span>}
              </div>
            ))}
            <div style={{ display: "flex", gap: 14, marginTop: 12, flexWrap: "wrap" }} className="tp-note">
              <span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: "#f7b500", marginRight: 5 }} />Building basics (&lt;40%)</span>
              <span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: "#2e6ff2", marginRight: 5 }} />Getting stronger (40–70%)</span>
              <span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: "#12b76a", marginRight: 5 }} />Exam ready (&gt;70%)</span>
            </div>
          </section>
        )}
        <section className="tp-panel" data-testid="portal-history">
          <h2 className="tp-h2" style={{ fontSize: 20 }}>Quiz history</h2>
          {data.recent.length === 0 ? <p className="tp-muted" data-testid="portal-history-empty">No quizzes yet — your results will appear here after your first quiz.</p> : (
            <div style={{ marginTop: 12 }}>
              {data.recent.map((a, i) => (
                <Link key={a.attempt_id} to={`/portal/quiz/result/${a.attempt_id}`} className="tp-history-row" data-testid={`portal-history-${i}`}>
                  <span style={{ flex: 1, fontWeight: 600 }}>{a.topic_name}</span>
                  <span className="tp-pill info">{a.score}/{a.max_score} · {a.correct}/5 correct · {a.accuracy}%</span>
                  <span className="tp-note">{fmtDate(a.submitted_at)}</span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </>}
      {showFreeTest && <FreeTestModal onClose={() => setShowFreeTest(false)} />}
    </div>
  );
}

export default function PortalHome() {
  return <PortalShell><PortalHomeInner /></PortalShell>;
}
